import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Edit, Trash2, Ban, CheckCircle, Loader2, Copy, ExternalLink, Calendar, CreditCard, Package, Users, Clock, Tag, Building, DollarSign, XCircle, AlertCircle, Info } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { useAuthContext } from '@/contexts/AuthContext';

interface SubscriptionPlanPrice {
  id: string;
  subscription_plan_id: string;
  currency: string;
  price_amount: number;
  cost_amount?: number;
  margin?: number;
}

interface ExternalId {
  id: string;
  entity_name: string;
  entity_id: string;
  system_name: string;
  external_id: string;
  created_at: string;
  updated_at: string;
}

interface SubscriptionPlanFamily {
  id: string;
  name: string;
  description: string | null;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  family_id: string;
  price: number;
  currency: string;
  billing_frequency: string;
  sku?: string;
  vendor_id?: string;
  vendor_subscription_name?: string;
  vendor_part_number?: string;
  pricing_model?: string;
  unit_measure?: string;
  minimum_quantity?: number;
  maximum_quantity?: number;
  billing_model?: string;
  billing_period_type?: string;
  billing_period_duration?: number;
  subscription_period_type?: string;
  subscription_period_duration?: number;
  service_type?: string;
  organization_id?: string;
  billing_cycles?: string;
  trial_period_days?: number;
  imported?: boolean;
  prices?: SubscriptionPlanPrice[];
  family?: SubscriptionPlanFamily;
  created_at?: string;
  updated_at?: string;
}

export default function SubscriptionPlanDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuthContext();
  const [activeTab, setActiveTab] = useState("plano");

  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [externalIds, setExternalIds] = useState<ExternalId[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingExternalIds, setLoadingExternalIds] = useState(false);

  useEffect(() => {
    if (id) {
      fetchData();
      fetchExternalIds();
    }
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('subscription_plans')
        .select(`
          *,
          prices:subscription_plan_prices (*),
          family:subscription_plan_families (*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setPlan(data);
    } catch (error) {
      console.error('Error fetching plan:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar plano',
        description: 'Não foi possível carregar os detalhes do plano. Por favor, tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchExternalIds = async () => {
    try {
      setLoadingExternalIds(true);
      const { data, error } = await supabase
        .from('external_ids')
        .select('*')
        .eq('entity_name', 'subscription_plans')
        .eq('entity_id', id);

      if (error) throw error;
      setExternalIds(data || []);
    } catch (error) {
      console.error('Error fetching external IDs:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar IDs externos',
        description: 'Não foi possível carregar os IDs externos. Por favor, tente novamente.',
      });
    } finally {
      setLoadingExternalIds(false);
    }
  };

  const handleEdit = () => {
    navigate(`/subscription-plans/edit/${id}`);
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('subscription_plans')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Plano inativado',
        description: 'O plano foi inativado com sucesso.',
      });

      fetchData();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao inativar plano',
        description: 'Não foi possível inativar o plano. Por favor, tente novamente.',
      });
    }
  };

  const handleActivate = async () => {
    try {
      const { error } = await supabase
        .from('subscription_plans')
        .update({ is_active: true })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Plano ativado',
        description: 'O plano foi ativado com sucesso.',
      });

      fetchData();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao ativar plano',
        description: 'Não foi possível ativar o plano. Por favor, tente novamente.',
      });
    }
  };

  const handleDuplicate = async () => {
    if (!plan) return;
    
    try {
      // Criar um novo plano com TODOS os dados disponíveis, exceto o ID
      const newPlan = {
        name: `${plan.name} (Cópia)`,
        description: plan.description,
        family_id: plan.family_id,
        // Não copiar SKU - deixar o banco gerar um novo automaticamente
        sku: null,
        pricing_model: plan.pricing_model,
        unit_measure: plan.unit_measure,
        minimum_quantity: plan.minimum_quantity,
        maximum_quantity: plan.maximum_quantity,
        billing_model: plan.billing_model,
        billing_period_type: plan.billing_period_type,
        billing_frequency: plan.billing_period_type, // Sincronizar com billing_period_type
        billing_period_duration: plan.billing_period_duration,
        subscription_period_type: plan.subscription_period_type,
        subscription_period_duration: plan.subscription_period_duration,
        vendor_id: plan.vendor_id,
        vendor_subscription_name: plan.vendor_subscription_name,
        vendor_part_number: plan.vendor_part_number,
        service_type: plan.service_type || 'licensing',
        organization_id: plan.organization_id, // Copiar organization_id se existir
        is_active: true,
        // Campos de auditoria - preencher explicitamente para garantir
        created_by: user?.id,
        updated_by: user?.id,
        imported: false, // Reset import flag for cloned plan
        // Campos de preço (se existirem na tabela principal)
        price: plan.price,
        currency: plan.currency,
        // Campos de cobrança adicionais
        billing_cycles: plan.billing_cycles,
        trial_period_days: plan.trial_period_days,
      };

      // Inserir o novo plano
      const { data: newPlanData, error: planError } = await supabase
        .from('subscription_plans')
        .insert(newPlan)
        .select()
        .single();

      if (planError) throw planError;

      // Copiar os preços
      if (plan.prices && plan.prices.length > 0) {
        const pricePromises = plan.prices.map(price => {
          const newPrice = {
            subscription_plan_id: newPlanData.id,
            currency: price.currency,
            price_amount: price.price_amount,
            cost_amount: price.cost_amount,
            margin: price.margin,
          };
          
          return supabase
            .from('subscription_plan_prices')
            .insert(newPrice);
        });

        await Promise.all(pricePromises);
      }

      // Criar produto no HubSpot para o plano clonado
      try {
        const { data: prices } = await supabase
          .from('subscription_plan_prices')
          .select('currency, price_amount')
          .eq('subscription_plan_id', newPlanData.id)
          .in('currency', ['BRL', 'USD']);

        const brlPrice = prices?.find(p => p.currency === 'BRL')?.price_amount || 0;
        const usdPrice = prices?.find(p => p.currency === 'USD')?.price_amount || 0;

        // Mapear service_type para hs_product_type
        const getHubSpotProductType = (serviceType: string) => {
          const normalizedType = serviceType?.trim()?.toLowerCase();
          if (normalizedType === 'licensing') return 'license';
          if (normalizedType === 'material') return 'material';
          if (normalizedType === 'program') return 'program';
          if (normalizedType === 'professional_service') return 'service';
          return 'service'; // Default
        };

        // Mapear billing_period_type para recurringbillingfrequency
        const getRecurringBillingFrequency = (billingPeriodType: string) => {
          const frequencyMap: Record<string, string> = {
            'month': 'monthly',
            'year': 'annually',
            'quarter': 'quarterly',
            'semiannual': 'semiannually',
            'one-time': 'one-time'
          };
          return frequencyMap[billingPeriodType] || 'monthly';
        };

        // Montar payload para HubSpot
        const hubspotPayload: any = {
          name: newPlanData.name,
          description: newPlanData.description || '',
          // Só incluir SKU se existir
          ...(newPlanData.sku && { hs_sku: newPlanData.sku }),
          hs_product_type: getHubSpotProductType(newPlanData.service_type || 'licensing'),
          hs_price_brl: brlPrice.toString(),
          ...(usdPrice > 0 && { hs_price_usd: usdPrice.toString() }),
        };

        // Adicionar frequência de cobrança apenas se não for one-time
        if (newPlanData.billing_period_type !== 'one-time') {
          hubspotPayload.recurringbillingfrequency = getRecurringBillingFrequency(newPlanData.billing_period_type || 'month');
        }

        // Chamar Edge Function para criar produto no HubSpot
        console.log('🚀 Chamando Edge Function create-product-hubspot com payload:', hubspotPayload);
        
        const { data: hubspotResult, error: hubspotError } = await supabase.functions.invoke('create-product-hubspot', {
          body: hubspotPayload
        });

        console.log('📥 Resposta da Edge Function:', { hubspotResult, hubspotError });

        if (hubspotError) {
          console.error('Erro ao criar produto no HubSpot:', hubspotError);
          toast({
            variant: 'default',
            title: 'Aviso',
            description: 'Plano duplicado com sucesso, mas houve um erro ao criar no HubSpot.',
          });
        } else if (hubspotResult && (hubspotResult.id || hubspotResult.data?.id)) {
          // Extrair o ID do produto do HubSpot da resposta
          const hubspotProductId = hubspotResult.id || hubspotResult.data?.id;
          console.log('✅ Produto criado no HubSpot com sucesso:', hubspotProductId);
          
          // Salvar o ID do HubSpot na tabela external_ids
          const { error: externalIdError } = await supabase
            .from('external_ids')
            .insert({
              entity_name: 'subscription_plans',
              entity_id: newPlanData.id,
              system_name: 'hubspot',
              external_id: hubspotProductId
            });

          if (externalIdError) {
            console.error('Erro ao salvar ID do HubSpot:', externalIdError);
            toast({
              variant: 'default',
              title: 'Aviso',
              description: 'Produto criado no HubSpot, mas houve um erro ao salvar a referência.',
            });
          } else {
            console.log('✅ Referência do HubSpot salva com sucesso');
            toast({
              title: 'Sucesso',
              description: 'Plano duplicado e sincronizado com HubSpot com sucesso!',
            });
          }
        } else {
          console.warn('Resposta do HubSpot não contém ID do produto:', hubspotResult);
          console.log('🔍 Estrutura completa da resposta:', JSON.stringify(hubspotResult, null, 2));
          toast({
            variant: 'default',
            title: 'Aviso',
            description: 'Plano duplicado com sucesso, mas resposta do HubSpot não contém ID do produto.',
          });
        }

      } catch (hubspotError) {
        console.error('Erro na integração com HubSpot:', hubspotError);
        // Não falhar o processo de clonagem por erro no HubSpot
      }

      toast({
        title: 'Plano duplicado',
        description: 'O plano foi duplicado com sucesso.',
      });

      // Redirecionar para a página de edição do novo plano
      navigate(`/subscription-plans/edit/${newPlanData.id}`);
    } catch (error) {
      console.error('Erro ao duplicar plano:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao duplicar plano',
        description: 'Não foi possível duplicar o plano. Por favor, tente novamente.',
      });
    }
  };

  const formatBillingFrequency = (frequency: string) => {
    switch (frequency) {
      case 'month':
        return 'Mensal';
      case 'quarter':
        return 'Trimestral';
      case 'semiannual':
        return 'Semestral';
      case 'year':
        return 'Anual';
      case 'one-time':
        return 'Pagamento Único';
      default:
        return frequency;
    }
  };

  const formatBillingModel = (model?: string) => {
    if (!model) return '-';
    
    switch (model) {
      case 'on creation':
        return 'Na criação';
      case 'on activation':
        return 'Na ativação';
      case 'on usage':
        return 'No uso';
      case 'subscription':
        return 'Assinatura';
      case 'one-time':
        return 'Pagamento Único';
      default:
        return model;
    }
  };

  const formatPricingModel = (model?: string, unitMeasure?: string) => {
    if (!model) return '-';
    
    switch (model) {
      case 'flat':
        return 'Valor Fixo';
      case 'per unit':
        return `Por Unidade${unitMeasure ? ` (${formatUnitMeasure(unitMeasure)})` : ''}`;
      default:
        return model;
    }
  };

  const formatBillingPeriod = (type?: string, duration?: number) => {
    if (!type || !duration) return '-';
    
    const typeLabel = type === 'month' ? 'Mês(es)' : type === 'year' ? 'Ano(s)' : type;
    return `${duration} ${typeLabel}`;
  };

  const formatUnitMeasure = (unitMeasure?: string) => {
    if (!unitMeasure) return '-';
    
    const unitMap: Record<string, string> = {
      'user': 'Por usuário',
      'hour': 'Por hora',
      'gb': 'Por GB',
      'license': 'Por licença',
      'seat': 'Por assento',
      'instance': 'Por instância',
      'api_call': 'Por chamada de API',
      'transaction': 'Por transação'
    };
    
    return unitMap[unitMeasure.toLowerCase()] || unitMeasure;
  };

  if (loading) {
    return (
      <div className="w-full px-6 py-6">
        <div className="flex justify-center items-center h-[60vh]">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Carregando dados do plano...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="w-full px-6 py-6">
        <div className="flex justify-center items-center h-[60vh]">
          <p className="text-muted-foreground">Plano não encontrado.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 pl-3 lg:pl-4">
      {/* Modern Header with Gradient Background */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
        
        <div className="relative container mx-auto px-6 py-6 pb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="space-y-2">
        <div className="flex items-center gap-3">
          <Button 
                  variant="secondary" 
                  size="default" 
            onClick={() => navigate('/subscription-plans')}
                  className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
          >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Voltar
          </Button>
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                  <Package className="h-6 w-6 text-white" />
                </div>
          <div>
            <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-extrabold text-white">{plan.name}</h1>
                    <Badge 
                      variant={plan.is_active ? "default" : "secondary"}
                      className={plan.is_active ? "bg-green-500 hover:bg-green-600" : "bg-gray-500 hover:bg-gray-600"}
                    >
                {plan.is_active ? "Ativo" : "Inativo"}
              </Badge>
            </div>
                  <p className="text-blue-100 text-base pb-2">
                    Visualização completa do plano de assinatura
            </p>
          </div>
        </div>
            </div>

        <div className="flex items-center gap-2">
          <Button
                variant="secondary"
                size="default"
                className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30"
            onClick={plan.is_active ? handleDelete : handleActivate}
          >
            {plan.is_active ? (
              <>
                    <Ban className="h-4 w-4 mr-2" />
                Inativar
              </>
            ) : (
              <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                Ativar
              </>
            )}
          </Button>
          {!plan.imported && (
            <Button
                  variant="secondary"
                  size="default"
                  className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30"
              onClick={handleDuplicate}
            >
                  <Copy className="h-4 w-4 mr-2" /> Duplicar
            </Button>
          )}
          <Button
                size="default"
            onClick={handleEdit}
                className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
          >
                <Edit className="h-4 w-4 mr-2" /> Editar
          </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10">

        {/* Enhanced Main Content with Modern Design */}
        <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm mb-8">
          <CardContent className="p-6">
            {/* Navigation Tabs */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('plano')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'plano'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Package className="h-4 w-4" />
                  Plano
                </button>
                <button
                  onClick={() => setActiveTab('precos')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'precos'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <DollarSign className="h-4 w-4" />
              Preços
                </button>
                <button
                  onClick={() => setActiveTab('cobranca')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'cobranca'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <CreditCard className="h-4 w-4" />
              Cobrança
                </button>
                <button
                  onClick={() => setActiveTab('fornecedor')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'fornecedor'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Building className="h-4 w-4" />
              Fornecedor
                </button>
                <button
                  onClick={() => setActiveTab('external_ids')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'external_ids'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <ExternalLink className="h-4 w-4" />
              IDs Externos
                </button>
              </div>
        </div>

            <div className="space-y-6">
          {/* Seção de Plano */}
          {activeTab === "plano" && (
                <div className="space-y-6">
                <div className="space-y-1">
                  <p className="text-sm font-extrabold text-muted-foreground">Nome</p>
                  <p className="text-base">{plan.name}</p>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-extrabold text-muted-foreground">Descrição</p>
                  <p className="text-base">{plan.description || "-"}</p>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-extrabold text-muted-foreground">Família</p>
                  <p className="text-base">{plan.family?.name || "-"}</p>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-extrabold text-muted-foreground">SKU</p>
                  <p className="text-base">{plan.sku || "-"}</p>
                </div>

                <Separator />

                <div className="space-y-1">
                  <p className="text-sm font-extrabold text-muted-foreground">Data de Criação</p>
                  <p className="text-base">
                    {plan.created_at ? new Date(plan.created_at).toLocaleString() : "-"}
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-extrabold text-muted-foreground">Última Atualização</p>
                  <p className="text-base">
                    {plan.updated_at ? new Date(plan.updated_at).toLocaleString() : "-"}
                  </p>
                </div>
                </div>
          )}
          
          {/* Seção de Preços */}
          {activeTab === "precos" && (
                <div className="space-y-6">
                {plan.prices && plan.prices.length > 0 ? (
                  <div className="rounded-md border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Moeda</TableHead>
                          <TableHead>Preço</TableHead>
                          <TableHead>Custo</TableHead>
                          <TableHead>Margem (%)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {plan.prices.map((price) => (
                          <TableRow key={price.id}>
                            <TableCell>{price.currency}</TableCell>
                            <TableCell>{formatCurrency(price.price_amount, price.currency)}</TableCell>
                            <TableCell>
                              {price.cost_amount ? formatCurrency(price.cost_amount, price.currency) : "-"}
                            </TableCell>
                            <TableCell>{price.margin ? `${price.margin.toFixed(2)}%` : "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="bg-muted/40 p-4 rounded-lg text-center">
                    <p className="text-muted-foreground">Nenhum preço configurado para este plano.</p>
                  </div>
                )}

                <Separator />

                <div className="space-y-1">
                  <p className="text-sm font-extrabold text-muted-foreground">Modelo de Precificação</p>
                  <p className="text-base">{formatPricingModel(plan.pricing_model, plan.unit_measure)}</p>
                </div>

                {plan.pricing_model === 'per unit' && (
                  <div className="bg-muted/40 p-4 rounded-lg space-y-4">
                    <div className="space-y-1">
                      <p className="text-sm font-extrabold text-muted-foreground">Unidade de Medida</p>
                      <p className="text-base">{formatUnitMeasure(plan.unit_measure)}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-sm font-extrabold text-muted-foreground">Quantidade Mínima</p>
                        <p className="text-base">{plan.minimum_quantity || "-"}</p>
                      </div>

                      <div className="space-y-1">
                        <p className="text-sm font-extrabold text-muted-foreground">Quantidade Máxima</p>
                        <p className="text-base">{plan.maximum_quantity || "-"}</p>
                      </div>
                    </div>
                  </div>
                )}
                </div>
          )}
          
          {/* Seção de Cobrança */}
          {activeTab === "cobranca" && (
                <div className="space-y-6">
                <div className="space-y-1">
                  <p className="text-sm font-extrabold text-muted-foreground">Modelo de Cobrança</p>
                  <p className="text-base">{formatBillingModel(plan.billing_model)}</p>
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <p className="text-sm font-extrabold text-muted-foreground">Período de Cobrança</p>
                    <p className="text-base">{formatBillingFrequency(plan.billing_period_type)}</p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm font-extrabold text-muted-foreground">Duração do Período de Cobrança</p>
                    <p className="text-base">
                      {formatBillingPeriod(plan.billing_period_type, plan.billing_period_duration)}
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <p className="text-sm font-extrabold text-muted-foreground">Período de Assinatura</p>
                    <p className="text-base">
                      {plan.subscription_period_type === 'month' ? 'Mensal' : 'Anual'}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm font-extrabold text-muted-foreground">Duração do Período de Assinatura</p>
                    <p className="text-base">
                      {formatBillingPeriod(plan.subscription_period_type, plan.subscription_period_duration)}
                    </p>
                  </div>
                </div>
                </div>
              )}
              
              {/* Seção de Fornecedor */}
              {activeTab === "fornecedor" && (
                <div className="space-y-6">
                {plan.vendor_id ? (
                  <div className="bg-muted/40 p-4 rounded-lg space-y-6">
                    <div className="space-y-1">
                      <p className="text-sm font-extrabold text-muted-foreground">
                        Nome da Assinatura no Fornecedor
                      </p>
                      <p className="text-base">{plan.vendor_subscription_name || "-"}</p>
                    </div>

                    <div className="space-y-1">
                      <p className="text-sm font-extrabold text-muted-foreground">
                        Número da Parte no Fornecedor
                      </p>
                      <p className="text-base">{plan.vendor_part_number || "-"}</p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-muted/40 p-4 rounded-lg text-center">
                    <p className="text-muted-foreground">Nenhum fornecedor associado a este plano.</p>
                  </div>
                )}
                </div>
              )}
              
              {/* Seção de IDs Externos */}
              {activeTab === "external_ids" && (
                <div className="space-y-6">
                {loadingExternalIds ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      <p className="text-muted-foreground">Carregando IDs externos...</p>
                    </div>
                  </div>
                ) : externalIds.length > 0 ? (
                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Sistema</TableHead>
                          <TableHead>ID Externo</TableHead>
                          <TableHead>Data de Criação</TableHead>
                          <TableHead>Última Atualização</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {externalIds.map((extId) => (
                          <TableRow key={extId.id}>
                            <TableCell className="font-extrabold">{extId.system_name}</TableCell>
                            <TableCell>{extId.external_id}</TableCell>
                            <TableCell>{new Date(extId.created_at).toLocaleString()}</TableCell>
                            <TableCell>{new Date(extId.updated_at).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="bg-muted/40 rounded-lg p-8 text-center">
                    <p className="text-muted-foreground mb-4">Nenhum ID externo associado a este plano.</p>
                    <p className="text-sm text-muted-foreground">
                      IDs externos são criados automaticamente quando este plano é integrado com sistemas externos.
                    </p>
                  </div>
                )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bottom Spacing */}
        <div className="h-16" />
      </div>
    </div>
  );
} 